import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Loader2, Copy, Check } from 'lucide-react';
import type { CompareResponse } from '../types';

const TYPE_BADGE: Record<string, string> = {
  'change':         'bg-yellow-100 text-yellow-800',
  'addition':       'bg-green-100 text-green-800',
  'deletion':       'bg-red-100 text-red-800',
  'missing':        'bg-gray-100 text-gray-800',
  'изменение':      'bg-yellow-100 text-yellow-800',
  'добавление':     'bg-green-100 text-green-800',
  'удаление':       'bg-red-100 text-red-800',
  'отсутствует':    'bg-gray-100 text-gray-800',
  'информация':     'bg-blue-100 text-blue-800',
  'information':    'bg-blue-100 text-blue-800',
  'ошибка':         'bg-red-100 text-red-800',
  'error':          'bg-red-100 text-red-800',
  'форматирование': 'bg-indigo-100 text-indigo-800',
  'formatting':     'bg-indigo-100 text-indigo-800',
};

function isFormattingType(type: string): boolean {
  return type === 'форматирование' || type === 'formatting';
}

function getTypeBadge(type: string): string {
  return TYPE_BADGE[type] || 'bg-blue-100 text-blue-800';
}

function getTypeLabel(type: string): string {
  if (type === 'информация' || type === 'information') return 'инфо';
  if (type === 'ошибка' || type === 'error') return 'ошибка';
  return type;
}

interface Props {
  results: CompareResponse;
  onSwapAndRetry: () => void;
}

export default function CompareResults({ results, onSwapAndRetry }: Props) {
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(key);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {}
  };

  const isProhibited =
    results.differences.length === 1 &&
    (results.differences[0].type === 'ошибка' || results.differences[0].type === 'error') &&
    results.differences[0].description?.includes('PROHIBITED_CONTENT');

  // Stable sort: formatting-type differences go to the very end, preserving
  // the original order otherwise (errors stay first, chunks keep their order).
  const sortedDifferences = results.differences
    .map((d, i) => ({ d, i }))
    .sort((a, b) => {
      const af = isFormattingType(a.d.type) ? 1 : 0;
      const bf = isFormattingType(b.d.type) ? 1 : 0;
      if (af !== bf) return af - bf;
      return a.i - b.i;
    })
    .map(x => x.d);

  return (
    <div className={`bg-white rounded-xl shadow-lg border ${results.is_partial ? 'border-amber-300' : 'border-gray-200'} overflow-hidden`}>
      {/* Header */}
      <div className={`p-4 ${results.is_partial ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : 'bg-gradient-to-r from-pink-500 to-rose-500'} text-white`}>
        <h2 className="text-xl font-bold">
          {results.is_partial ? 'Gemini обрабатывает...' : 'Результаты сравнения'}
        </h2>
        <p className="text-white/80 text-sm">{results.message}</p>
        {results.is_partial && (
          <p className="text-white/60 text-xs mt-1">Порядок может измениться после полной обработки</p>
        )}
        {results.retries > 0 && (
          <p className="text-white/80 text-xs mt-1">
            Нейронка ступила {results.retries} раз(а), но мы её перезапустили и всё получилось! Или не получилось, увы.
          </p>
        )}
        {results.chunks_processed && results.chunks_processed > 1 && !results.is_partial && (
          <p className="text-white/80 text-xs mt-1">Документы были разбиты на {results.chunks_processed} частей из-за большого размера</p>
        )}
      </div>

      {/* Body */}
      {isProhibited ? (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Политика безопасности Google</h3>
          <p className="text-gray-600 mb-4 max-w-lg mx-auto">
            Gemini заблокировал содержимое документов как потенциально небезопасное.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto mb-4">
            <p className="text-amber-800 font-medium mb-2">Попробуйте:</p>
            <ul className="text-amber-700 text-sm text-left space-y-1">
              <li>Поменять файлы местами (загрузить в другом порядке)</li>
              <li>Удалить персональные данные из документов</li>
              <li>Разбить большие документы на части</li>
              <li>Попробовать ещё раз</li>
            </ul>
          </div>
          <button onClick={onSwapAndRetry} className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-medium rounded-lg hover:from-pink-600 hover:to-rose-600 transition shadow-lg">
            Поменять файлы местами
          </button>
        </div>
      ) : results.differences.length === 0 ? (
        <div className="p-8 text-center">
          {results.is_empty ? (
            <>
              <Loader2 className="w-12 h-12 text-pink-500 mx-auto mb-3 animate-spin" />
              <h3 className="text-lg font-medium text-gray-800">Ожидаем результаты...</h3>
              <p className="text-gray-500">Чанки ещё обрабатываются.</p>
            </>
          ) : (
            <>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-800">Различий не найдено</h3>
              <p className="text-gray-500">Документы идентичны или очень похожи</p>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Раздел/Пункт</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Тип</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Содержимое в документах</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Описание</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedDifferences.map((diff, i) => (
                <tr key={i} className={`hover:bg-gray-50 ${diff._isPartial ? 'bg-amber-50/50' : ''}`}>
                  <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                    <div className="flex items-center gap-2">
                      {diff._isPartial && <span className="text-amber-500" title="Порядок может измениться">&#x23F3;</span>}
                      {diff.section}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeBadge(diff.type)}`}>
                      {getTypeLabel(diff.type)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-3 min-w-[400px] max-w-2xl">
                      {diff.documents && Object.entries(diff.documents).map(([docName, content], di) => {
                        const key = `${i}-${di}`;
                        return (
                          <div key={docName} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-semibold text-gray-800 text-sm">{docName}</span>
                              <button
                                onClick={() => copyToClipboard(content, key)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-pink-50 hover:border-pink-300 transition"
                              >
                                {copiedIndex === key ? (
                                  <><Check className="w-4 h-4 text-green-500" /><span className="text-green-600">Скопировано</span></>
                                ) : (
                                  <><Copy className="w-4 h-4 text-gray-500" /><span className="text-gray-600">Копировать</span></>
                                )}
                              </button>
                            </div>
                            <div className="bg-white p-4 rounded-lg border border-gray-100 text-gray-800 text-base leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">
                              {content}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{diff.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
